<template>
  <div class="chart-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import { Bar, mixins } from 'vue-chartjs'
const { reactiveProp } = mixins

export default {
  name: 'SagYieldNGChart',
  extends: Bar,
  mixins: [reactiveProp],
  props: {
    chartData: {
      type: Object,
      required: true,
      validator: (value) => {
        return value.Yield && value.NG
      }
    }
  },
  data() {
    return {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          xAxes: [{
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: '日期'
            },
            ticks: {
              callback: function(value) {
                const date = new Date(value)
                return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
              }
            }
          }],
          yAxes: [{
            id: 'y-axis-quantity',
            position: 'left',
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: '數量'
            },
            ticks: {
              callback: function(value) {
                return new Intl.NumberFormat().format(value)
              }
            }
          }, {
            id: 'y-axis-percentage',
            position: 'right',
            scaleLabel: {
              display: true,
              labelString: '百分比'
            },
            ticks: {
              callback: function(value) {
                return value + '%'
              },
              min: 0,
              max: 100
            },
            gridLines: {
              drawOnChartArea: false
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const value = tooltipItem.yLabel
              const label = dataset.label || ''
              
              if (dataset.yAxisID === 'y-axis-percentage') {
                return `${label}: ${value}%`
              }
              return `${label}: ${new Intl.NumberFormat().format(value)}`
            }
          }
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  },
  computed: {
    chartDataComputed() {
      // Get unique dates and sort them
      const dates = [...new Set([
        ...this.chartData.Yield.map(item => item.Date),
        ...this.chartData.NG.map(item => item.Date)
      ])].sort()

      // Process Yield data (percentage lines)
      const yieldLabels = [...new Set(this.chartData.Yield.map(item => item.Label))]
      const yieldDatasets = yieldLabels.map(label => {
        const data = dates.map(date => {
          const item = this.chartData.Yield.find(d => d.Date === date && d.Label === label)
          return item ? parseFloat(item.Value.replace('%', '')) : null
        })
        return {
          type: 'line',
          label,
          data,
          borderColor: this.getRandomColor(),
          borderWidth: 2,
          fill: false,
          yAxisID: 'y-axis-percentage',
          pointRadius: 5,
          pointHoverRadius: 8,
          pointBackgroundColor: this.getRandomColor()
        }
      })

      // Process NG data (stacked bars)
      const ngLabels = [...new Set(this.chartData.NG.map(item => item.Label))]
      const ngDatasets = ngLabels.map(label => {
        const data = dates.map(date => {
          const item = this.chartData.NG.find(d => d.Date === date && d.Label === label)
          return item ? parseInt(item.Value) : 0
        })
        return {
          type: 'bar',
          label,
          data,
          backgroundColor: this.getRandomColor(0.7),
          yAxisID: 'y-axis-quantity',
          stack: 'Stack 0'
        }
      })

      return {
        labels: dates,
        datasets: [...ngDatasets, ...yieldDatasets]
      }
    }
  },
  methods: {
    getRandomColor(alpha = 1) {
      const colors = [
        `rgba(255, 99, 132, ${alpha})`,
        `rgba(54, 162, 235, ${alpha})`,
        `rgba(255, 206, 86, ${alpha})`,
        `rgba(75, 192, 192, ${alpha})`,
        `rgba(153, 102, 255, ${alpha})`,
        `rgba(255, 159, 64, ${alpha})`
      ]
      return colors[Math.floor(Math.random() * colors.length)]
    }
  },
  mounted() {
    this.renderChart(this.chartDataComputed, this.options)
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        if (this.$data._chart) {
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartDataComputed, this.options)
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  height: 400px;
}
</style>
